import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Tooltip, IconButton, Menu, Divider } from '@mui/material';
import { useConfirm } from '../components/common/ConfirmDialog';
import api from '../services/api';
import { useAppTheme } from '../hooks/useAppTheme';

interface LogEntry {
  id: number;
  timestamp: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG';
  category: string;
  message: string;
  details?: string;
  userId?: number;
  sessionId?: string;
}

// Saved-filter views (Phase F)
const VIEWS_KEY = 'logs.savedViews.v1';
interface SavedLogView {
  id: string;
  name: string;
  level: string;
  textFilter: string;
  fromDate: string;
  toDate: string;
  limit: number;
}
const loadViews = (): SavedLogView[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(VIEWS_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
};
const persistViews = (vs: SavedLogView[]) => {
  try { localStorage.setItem(VIEWS_KEY, JSON.stringify(vs)); } catch { /* ignore */ }
};

const LogsPage: React.FC = () => {
  const { C } = useAppTheme();
  const confirm = useConfirm();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState<string>('all');
  const [limit, setLimit] = useState<number>(100);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  // Client-side filters
  const [textFilter, setTextFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Saved views state
  const [savedViews, setSavedViews] = useState<SavedLogView[]>(loadViews());
  const [viewsAnchor, setViewsAnchor] = useState<HTMLElement | null>(null);
  const [saveViewName, setSaveViewName] = useState<string>('');

  const saveCurrentView = () => {
    const name = saveViewName.trim() || `View ${savedViews.length + 1}`;
    const v: SavedLogView = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name, level, textFilter, fromDate, toDate, limit,
    };
    const next = [v, ...savedViews].slice(0, 12);
    setSavedViews(next);
    persistViews(next);
    setSaveViewName('');
  };
  const applyView = (v: SavedLogView) => {
    setLevel(v.level); setTextFilter(v.textFilter);
    setFromDate(v.fromDate); setToDate(v.toDate); setLimit(v.limit);
    setViewsAnchor(null);
  };
  const deleteView = async (v: SavedLogView) => {
    const ok = await confirm({ title: `Delete view "${v.name}"?`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    const next = savedViews.filter(x => x.id !== v.id);
    setSavedViews(next);
    persistViews(next);
  };

  // Cleanup state
  const [cleanupDays, setCleanupDays] = useState<number>(30);
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<string | null>(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params: any = { limit };
      if (level !== 'all') params.level = level;

      const response = await api.get<{ logs: LogEntry[] }>('/logs', { params });
      const data = response.data as { logs: LogEntry[] };
      setLogs(data.logs || []);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // Auto-refresh every 15 minutes
    const interval = setInterval(fetchLogs, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [level, limit]);

  const handleCleanup = async () => {
    try {
      setCleaning(true);
      const response = await api.delete(`/logs/cleanup?days=${cleanupDays}`);
      const data = response.data as { deleted: number };
      setCleanupResult(`Deleted ${data.deleted} log entries older than ${cleanupDays} days.`);
      setCleanupDialogOpen(false);
      fetchLogs();
    } catch (error: any) {
      setCleanupResult(`Error: ${error.response?.data?.error?.message || 'Cleanup failed'}`);
    } finally {
      setCleaning(false);
    }
  };

  const getLogLevelColor = (logLevel: string) => {
    switch (logLevel) {
      case 'ERROR':   return C.bear;
      case 'WARNING': return C.warn;
      case 'INFO':    return C.neutral;
      case 'DEBUG':   return C.accent;
      default:        return C.dim;
    }
  };

  const getLogLevelBgColor = (logLevel: string) => {
    switch (logLevel) {
      case 'ERROR':   return C.bearMuted;
      case 'WARNING': return C.warnMuted;
      case 'INFO':    return C.neutralMuted;
      case 'DEBUG':   return C.accentMuted;
      default:        return 'transparent';
    }
  };

  const getLogLevelIcon = (logLevel: string) => {
    switch (logLevel) {
      case 'ERROR': return <ErrorIcon sx={{ fontSize: 18 }} />;
      case 'WARNING': return <WarningIcon sx={{ fontSize: 18 }} />;
      case 'INFO': return <InfoIcon sx={{ fontSize: 18 }} />;
      case 'DEBUG': return <BugReportIcon sx={{ fontSize: 18 }} />;
      default: return <InfoIcon sx={{ fontSize: 18 }} />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" fontWeight="bold">
          System Logs
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Saved filter views">
            <IconButton size="small" onClick={(e) => setViewsAnchor(e.currentTarget)}>
              <BookmarksIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={viewsAnchor}
            open={Boolean(viewsAnchor)}
            onClose={() => setViewsAnchor(null)}
            PaperProps={{ sx: { minWidth: 260 } }}
          >
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                placeholder="View name…"
                value={saveViewName}
                onChange={(e) => setSaveViewName(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Tooltip title="Save current filters as a new view">
                <IconButton size="small" color="primary" onClick={saveCurrentView}>
                  <SaveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider />
            {savedViews.length === 0 ? (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Set up filters above and save them as a named view for one-click recall.
                </Typography>
              </Box>
            ) : (
              savedViews.map((v) => (
                <MenuItem key={v.id} onClick={() => applyView(v)} sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{v.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
                      {v.level} · {v.textFilter ? `"${v.textFilter}" · ` : ''}{v.limit} rows
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteView(v); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </MenuItem>
              ))
            )}
          </Menu>
          <TextField
            size="small"
            placeholder="Search in message/details…"
            value={textFilter}
            onChange={e => setTextFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="From"
            type="datetime-local"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            size="small"
            label="To"
            type="datetime-local"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select value={level} label="Level" onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="INFO">Info</MenuItem>
              <MenuItem value="WARNING">Warning</MenuItem>
              <MenuItem value="ERROR">Error</MenuItem>
              <MenuItem value="DEBUG">Debug</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Limit</InputLabel>
            <Select value={limit} label="Limit" onChange={(e) => setLimit(Number(e.target.value))}>
              <MenuItem value={50}>50 logs</MenuItem>
              <MenuItem value={100}>100 logs</MenuItem>
              <MenuItem value={200}>200 logs</MenuItem>
              <MenuItem value={500}>500 logs</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setCleanupDialogOpen(true)}
            size="small"
          >
            Cleanup Old Logs
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchLogs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              System logs — Auto-refreshes every 15 minutes
            </Typography>
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary">
                Last refreshed: {lastRefreshed.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Total: ${logs.length}`} size="small" variant="outlined" />
            <Chip label={`Errors: ${logs.filter(l => l.level === 'ERROR').length}`} color="error" size="small" />
            <Chip label={`Warnings: ${logs.filter(l => l.level === 'WARNING').length}`} color="warning" size="small" />
            <Chip label={`Info: ${logs.filter(l => l.level === 'INFO').length}`} color="info" size="small" />
          </Box>
        </Box>

        {cleanupResult && (
          <Box sx={{ mb: 2, p: 1.5, backgroundColor: C.bullMuted, borderRadius: 1 }}>
            <Typography variant="body2" color="success.main">{cleanupResult}</Typography>
          </Box>
        )}

        {(() => {
          const fromMs = fromDate ? new Date(fromDate).getTime() : null;
          const toMs = toDate ? new Date(toDate).getTime() : null;
          const q = textFilter.trim().toLowerCase();
          const filteredLogs = logs.filter(l => {
            if (fromMs != null && new Date(l.timestamp).getTime() < fromMs) return false;
            if (toMs != null && new Date(l.timestamp).getTime() > toMs) return false;
            if (q) {
              const hay = `${l.message ?? ''} ${l.details ?? ''} ${l.category ?? ''}`.toLowerCase();
              if (!hay.includes(q)) return false;
            }
            return true;
          });
          return loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredLogs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No logs found</Typography>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '100px 180px 120px 1fr 100px',
              gap: 2, fontWeight: 'bold',
              borderBottom: 2, borderColor: 'divider',
              pb: 1.5, backgroundColor: 'action.hover', p: 1.5, borderRadius: 1, mb: 1
            }}>
              <Box>Level</Box><Box>Timestamp</Box><Box>Category</Box><Box>Message</Box><Box>User ID</Box>
            </Box>
            <Box sx={{ maxHeight: 'calc(100vh - 430px)', overflowY: 'auto' }}>
              {filteredLogs.map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '100px 180px 120px 1fr 100px',
                    gap: 2, borderBottom: 1, borderColor: 'divider',
                    py: 1.5, px: 1.5,
                    backgroundColor: getLogLevelBgColor(log.level),
                    borderRadius: 1, mb: 0.5, transition: 'all 0.2s',
                    '&:hover': {
                      filter: 'brightness(0.92)',
                      transform: 'translateX(2px)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      icon={getLogLevelIcon(log.level)}
                      label={log.level}
                      size="small"
                      sx={{ bgcolor: getLogLevelBgColor(log.level), color: getLogLevelColor(log.level), border: `1px solid ${getLogLevelColor(log.level)}55`, fontWeight: 700, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{formatTimestamp(log.timestamp)}</Box>
                  <Box sx={{ fontSize: '0.85rem', fontWeight: 500, color: 'text.primary' }}>{log.category}</Box>
                  <Box sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                    {log.message}
                    {log.details && (
                      <Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary', fontFamily: 'monospace', backgroundColor: 'action.hover', p: 0.5, borderRadius: 0.5, wordBreak: 'break-all' }}>
                        {log.details}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{log.userId || '-'}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
        })()}
      </Paper>

      {/* Cleanup Dialog */}
      <Dialog open={cleanupDialogOpen} onClose={() => setCleanupDialogOpen(false)}>
        <DialogTitle>Clean Up Old Logs</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Delete log entries older than the specified number of days. This cannot be undone.
          </DialogContentText>
          <TextField
            label="Delete logs older than (days)"
            type="number"
            value={cleanupDays}
            onChange={(e) => setCleanupDays(Math.max(1, Number(e.target.value)))}
            inputProps={{ min: 1 }}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCleanup} color="error" variant="contained" disabled={cleaning}>
            {cleaning ? 'Deleting...' : `Delete logs older than ${cleanupDays} days`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogsPage;
